'use client';

import { Check, Clock } from '@phosphor-icons/react/ssr';
import {
  type ComponentProps,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover';
import {
  parseTimeInput,
  TIME_INPUT_MESSAGES,
  type TimeInputError,
  timeSlots,
} from './date-format';
import { pickerFieldStyles, timeListStyles } from './date-time-styles';
import { useFieldControl, useFieldLabelId } from './field';

const SLOT_HEIGHT = 32;

/** Scroll a list so `index` is the third visible row (Figma: 13:30, 14:00, [14:30]). */
function scrollToSlot(list: HTMLElement | null, index: number) {
  if (list && index >= 0) list.scrollTop = Math.max(0, (index - 2) * SLOT_HEIGHT);
}

export type TimeListProps = Omit<ComponentProps<'div'>, 'onSelect'> & {
  /** Slots, "HH:mm". Defaults to every 30 minutes. */
  options?: string[];
  /** The selected slot (Pale Sage + check). */
  value?: string;
  /** The keyboard-active slot (bg/muted), for `aria-activedescendant`. */
  activeValue?: string;
  onSelect?: (value: string) => void;
  /** Id of each option element, for `aria-activedescendant`. */
  optionId?: (value: string) => string;
  /** Draw only the listbox, without the panel (inside a popover that has one). */
  bare?: boolean;
};

/**
 * Scrollable list of time slots (Figma: Date & Time ↳ Time List, 93:11618).
 * A `listbox` of `option`s; opens scrolled so the selected slot shows. The
 * Time Field drives it from its input (arrows + `aria-activedescendant`).
 */
export function TimeList({
  options = timeSlots(30),
  value,
  activeValue,
  onSelect,
  optionId,
  bare = false,
  className,
  ...props
}: TimeListProps) {
  const styles = timeListStyles();
  const ref = useRef<HTMLDivElement>(null);
  const autoId = useId();
  const idOf = optionId ?? ((slot: string) => `${autoId}-${slot.replace(':', '')}`);
  // On its own the list is the focus stop and tracks its own active slot;
  // inside a Time Field the input drives `activeValue`.
  const [ownActive, setOwnActive] = useState(activeValue ?? value);
  const active = bare ? activeValue : ownActive;
  const selectedIndex = value ? options.indexOf(value) : -1;
  useEffect(() => {
    // Only a panel scrolls itself; a bare list scrolls its popover.
    const scroller = bare ? (ref.current?.parentElement ?? null) : ref.current;
    scrollToSlot(scroller, selectedIndex);
    // On mount only: later selections shouldn't jump the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!bare && ownActive)
      document.getElementById(idOf(ownActive))?.scrollIntoView({ block: 'nearest' });
    // idOf is derived from the ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bare, ownActive]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = ownActive ? options.indexOf(ownActive) : -1;
    const to = (i: number) => {
      event.preventDefault();
      setOwnActive(options[Math.min(options.length - 1, Math.max(0, i))]);
    };
    if (event.key === 'ArrowDown') to(index + 1);
    else if (event.key === 'ArrowUp') to(index - 1);
    else if (event.key === 'Home') to(0);
    else if (event.key === 'End') to(options.length - 1);
    else if ((event.key === 'Enter' || event.key === ' ') && ownActive) {
      event.preventDefault();
      onSelect?.(ownActive);
    }
  };

  return (
    <div
      ref={ref}
      role="listbox"
      data-slot="time-list"
      tabIndex={bare ? undefined : 0}
      aria-activedescendant={!bare && active ? idOf(active) : undefined}
      onKeyDown={bare ? undefined : onKeyDown}
      className={cn(!bare && styles.panel(), className)}
      {...props}
    >
      {options.map((option) => {
        const selected = option === value;
        return (
          <div
            key={option}
            id={idOf(option)}
            role="option"
            aria-selected={selected}
            data-active={option === active || undefined}
            data-value={option}
            className={styles.option()}
            // Keep focus where it is (the field's input, or the list).
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!bare) setOwnActive(option);
              onSelect?.(option);
            }}
          >
            <span className={styles.label()}>{option}</span>
            {selected && <Check aria-hidden weight="bold" className={styles.check()} />}
          </div>
        );
      })}
    </div>
  );
}

export type TimeFieldProps = {
  /** "HH:mm", 24-hour. `null` = controlled and empty. */
  value?: string | null;
  defaultValue?: string;
  onValueChange?: (value: string | undefined) => void;
  /** Minutes between slots in the list: 15 or 30 (default). Any exact time can be typed. */
  step?: 15 | 30;
  /** Custom slots instead of `step`. */
  options?: string[];
  /** Defaults to "Time" (Figma). */
  placeholder?: string;
  /** Typed text failed validation (or passed again: `null`). Show it as the Field `error`. */
  onInputError?: (message: string | null, reason: TimeInputError | null) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  /** Needed when not inside a Field. */
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  /** Class for the box (width). */
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
};

/**
 * Time entry (Figma: Date & Time ↳ Time Field). 24-hour "14:30"; typing any
 * exact time is allowed ("2:30 pm", "1430"), validated on blur or Enter.
 * Click or ↓ opens the Time List below. The input is an ARIA combobox: focus
 * stays in it, ↑ / ↓ move the active slot, Enter picks it, Escape closes.
 * Show the timezone near the field when it matters (publishing).
 */
export function TimeField({
  value: valueProp,
  defaultValue,
  onValueChange,
  step = 30,
  options: optionsProp,
  placeholder = 'Time',
  onInputError,
  id,
  name,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  className,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  ...props
}: TimeFieldProps) {
  const options = optionsProp ?? timeSlots(step);
  const [inner, setInner] = useState(defaultValue);
  const value = valueProp === undefined ? inner : (valueProp ?? undefined);
  const shown = value ?? '';

  const [text, setText] = useState(shown);
  const [error, setError] = useState<TimeInputError | null>(null);
  const [prevShown, setPrevShown] = useState(shown);
  if (shown !== prevShown) {
    setPrevShown(shown);
    setText(shown);
    setError(null);
  }

  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const [active, setActive] = useState<string | undefined>(undefined);
  const setOpen = (next: boolean) => {
    // Open on the selected slot (or the first slot at / after the typed time).
    if (next) setActive(value ?? options.find((o) => o >= text) ?? options[0]);
    setOpenState(next);
    onOpenChange?.(next);
  };

  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const optionId = (slot: string) => `${listId}-${slot.replace(':', '')}`;
  const fieldLabelId = useFieldLabelId();
  const { disabled, ...control } = useFieldControl({
    id,
    disabled: props.disabled,
    'aria-invalid': ariaInvalid || Boolean(error) || undefined,
  });
  const styles = pickerFieldStyles();

  // Keep the active slot in view while arrowing.
  useEffect(() => {
    if (open && active)
      document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' });
    // optionId is derived from listId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const report = (next: TimeInputError | null) => {
    setError(next);
    onInputError?.(next && TIME_INPUT_MESSAGES[next], next);
  };

  const change = (next: string | undefined) => {
    setInner(next);
    onValueChange?.(next);
    setText(next ?? '');
    report(null);
  };

  const commit = () => {
    const typed = text.trim();
    if (typed === shown) return;
    if (!typed) return change(undefined);
    const parsed = parseTimeInput(typed);
    if (!parsed.ok) return report(parsed.error);
    change(parsed.value);
  };

  const move = (delta: number) => {
    const index = active
      ? options.indexOf(active)
      : options.findIndex((o) => o >= text) - (delta > 0 ? 1 : 0);
    const next = Math.min(options.length - 1, Math.max(0, index + delta));
    setActive(options[next]);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!open) setOpen(true);
        else move(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter':
        if (open && active) {
          event.preventDefault();
          change(active);
        } else commit();
        if (open) setOpen(false);
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        break;
      case 'Tab':
        if (open) setOpen(false);
        break;
    }
  };

  const openFromPointer = (event: ReactPointerEvent) => {
    if (disabled || event.button !== 0 || open) return;
    setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={boxRef}
          data-slot="time-field"
          data-open={open || undefined}
          className={styles.root({ className })}
          onPointerDown={openFromPointer}
        >
          <Clock aria-hidden className={styles.icon()} />
          <Input
            {...control}
            disabled={disabled}
            name={name}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={open && active ? optionId(active) : undefined}
            aria-autocomplete="none"
            aria-label={ariaLabel}
            autoComplete="off"
            inputMode="numeric"
            placeholder={placeholder}
            className={styles.input()}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              // Typing wins over the list: Enter then commits the text.
              setActive(undefined);
            }}
            onBlur={commit}
            onKeyDown={onKeyDown}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        // A positioned listbox, not a dialog; focus stays in the input.
        role={undefined}
        // The scroll container: focusable for axe, never tabbed to.
        tabIndex={-1}
        className="max-h-53 w-(--radix-popover-trigger-width) min-w-0 p-1.5"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (boxRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
      >
        <TimeList
          bare
          id={listId}
          aria-labelledby={fieldLabelId}
          aria-label={fieldLabelId ? undefined : (ariaLabel ?? 'Times')}
          options={options}
          value={value}
          activeValue={active}
          optionId={optionId}
          onSelect={(slot) => {
            change(slot);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
